import React, { useState, useRef } from 'react';
import Loading from '../components/Loading';
import ShareReading from '../components/ShareReading';
import { getPalmReading, isGeminiReady } from '../services/geminiService';
import { useLanguage } from '../context/LanguageContext';
import { FaCamera, FaUpload, FaHandPaper, FaTimes } from 'react-icons/fa';

export default function PalmReading() {
  const { language, t } = useLanguage();
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [palmType, setPalmType] = useState('right'); // 'left' or 'right'
  const [name, setName] = useState('');
  const [gender, setGender] = useState('');
  const [reading, setReading] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('Image size should be less than 10MB');
        return;
      }
      processImage(file);
    }
  };

  const processImage = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setImage(reader.result);
      setImagePreview(reader.result);
      setError('');
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const handleGetReading = async () => {
    if (!image) {
      setError(t('uploadPalmImage'));
      return;
    }
    if (!isGeminiReady()) {
      setError(t('apiKeyError'));
      return;
    }
    setLoading(true);
    setError('');
    setReading('');
    try {
      const result = await getPalmReading(image, palmType, name, gender, language);
      setReading(result);
    } catch (err) {
      setError(err.message || 'Failed to read palm. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="container">
        <div className="page-header">
          <h2>{t('palmReadingTitle')}</h2>
          <p>{t('palmReadingDesc')}</p>
        </div>

        {/* Palm Info Card */}
        <div className="palm-info-card">
          <div className="palm-diagram">
            <FaHandPaper className="palm-icon" />
            <div className="palm-lines">
              <span className="line heart">Heart Line</span>
              <span className="line head">Head Line</span>
              <span className="line life">Life Line</span>
              <span className="line fate">Fate Line</span>
            </div>
          </div>
          <div className="palm-tips">
            <h4>{t('palmTipsTitle')}</h4>
            <ul>
              <li>{t('palmTip1')}</li>
              <li>{t('palmTip2')}</li>
              <li>{t('palmTip3')}</li>
              <li>{t('palmTip4')}</li>
            </ul>
          </div>
        </div>

        {/* Palm Selection */}
        <div className="palm-selection">
          <h3>{t('selectPalmHand')}</h3>
          <div className="palm-hand-options">
            <button
              className={`palm-hand-btn ${palmType === 'left' ? 'selected' : ''}`}
              onClick={() => setPalmType('left')}
            >
              <span className="hand-icon">🤚</span>
              <span>{t('leftHand')}</span>
              <span className="hand-desc">{t('leftHandDesc')}</span>
            </button>
            <button
              className={`palm-hand-btn ${palmType === 'right' ? 'selected' : ''}`}
              onClick={() => setPalmType('right')}
            >
              <span className="hand-icon" style={{ transform: 'scaleX(-1)' }}>🤚</span>
              <span>{t('rightHand')}</span>
              <span className="hand-desc">{t('rightHandDesc')}</span>
            </button>
          </div>
        </div>

        {/* Optional Details */}
        <div className="form-section">
          <div className="form-row">
            <div className="form-group">
              <label>{t('yourName')} ({t('optional')})</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('enterYourName')}
              />
            </div>
            <div className="form-group">
              <label>{t('gender')} ({t('optional')})</label>
              <select value={gender} onChange={(e) => setGender(e.target.value)}>
                <option value="">{t('selectGender')}</option>
                <option value="male">{t('male')}</option>
                <option value="female">{t('female')}</option>
                <option value="other">{t('other')}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Image Upload Section */}
        <div className="palm-upload-section">
          <h3>{t('uploadPalmPhoto')}</h3>

          {!imagePreview ? (
            <div className="upload-options">
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <input
                type="file"
                ref={cameraInputRef}
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />

              <button
                className="upload-btn camera"
                onClick={() => cameraInputRef.current?.click()}
              >
                <FaCamera />
                <span>{t('takePhoto')}</span>
              </button>

              <button
                className="upload-btn gallery"
                onClick={() => fileInputRef.current?.click()}
              >
                <FaUpload />
                <span>{t('uploadFromGallery')}</span>
              </button>
            </div>
          ) : (
            <div className="image-preview-container">
              <img src={imagePreview} alt="Palm preview" className="palm-preview" />
              <button className="remove-image-btn" onClick={handleRemoveImage}>
                <FaTimes />
              </button>
              <p className="preview-label">
                {palmType === 'left' ? t('leftHand') : t('rightHand')} Palm
              </p>
            </div>
          )}
        </div>

        {/* Get Reading Button */}
        {image && (
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <button className="btn btn-primary" onClick={handleGetReading} disabled={loading}>
              {loading ? t('analyzingPalm') : t('getPalmReading')}
            </button>
          </div>
        )}

        {loading && <Loading message={t('loadingPalmReading')} />}
        {error && <div className="error-message">{error}</div>}

        {reading && (
          <div className="result-container">
            <div className="result-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3>🖐️ {t('palmReadingResult')}</h3>
                <ShareReading title="Palm Reading by AstroA2Z" content={reading} type="palm-reading" />
              </div>
              <div className="content">{reading}</div>
            </div>
          </div>
        )}

        {/* Palm Reading Guide */}
        <div className="palm-guide">
          <h3>{t('palmLinesGuide')}</h3>
          <div className="lines-grid">
            <div className="line-card">
              <span className="line-color heart"></span>
              <h4>{t('heartLine')}</h4>
              <p>{t('heartLineDesc')}</p>
            </div>
            <div className="line-card">
              <span className="line-color head"></span>
              <h4>{t('headLine')}</h4>
              <p>{t('headLineDesc')}</p>
            </div>
            <div className="line-card">
              <span className="line-color life"></span>
              <h4>{t('lifeLine')}</h4>
              <p>{t('lifeLineDesc')}</p>
            </div>
            <div className="line-card">
              <span className="line-color fate"></span>
              <h4>{t('fateLine')}</h4>
              <p>{t('fateLineDesc')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
